import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Tab, Tabs, Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { toast } from 'react-toastify';
import VenuePreviewModal from './VenuePreviewModal';

const VenueVerificationLayer = () => {
    const { auth } = useAuth();
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: 'pending',
        search: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [venueDetails, setVenueDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    
    // Action State
    const [actionModal, setActionModal] = useState(false);
    const [actionType, setActionType] = useState(null); // 'approved' | 'rejected'
    const [actionReason, setActionReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Preview State
    const [showPreview, setShowPreview] = useState(false);
    const [previewId, setPreviewId] = useState(null);

    // Bulk Import CSV State
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const getOwnerImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http')) return imagePath;
        if (imagePath.includes('/avatar/')) {
            return `${config.API_BASE_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
        }
        return imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    };

    // Fetch Stats
    const fetchStats = useCallback(async () => {
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/stats`);
            const data = await res.json();
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [auth]);

    // Fetch Venues
    const fetchVenues = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                status: filters.status === 'all' ? '' : filters.status,
                search: filters.search,
                page: filters.page,
                limit: filters.limit
            });
            
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues?${query}`);
            const data = await res.json();
            setVenues(data.data);
            setPagination(data.pagination);
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error('Failed to load venues');
        } finally {
            setLoading(false);
        }
    }, [auth, filters]);

    // Fetch Venue Details
    const fetchVenueDetails = async (id) => {
        setDetailsLoading(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${id}`);
            const data = await res.json();
            setVenueDetails(data);
        } catch (error) {
            console.error('Error fetching venue details:', error);
            toast.error('Failed to load venue details');
        } finally {
            setDetailsLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        fetchVenues();
    }, [fetchStats, fetchVenues]);

    const handleView = (venue) => {
        setSelectedVenue(venue);
        setShowModal(true);
        fetchVenueDetails(venue.id);
    };

    const handlePreview = (venue) => {
        setPreviewId(venue.id);
        setShowPreview(true);
    };

    const handleActionClick = (type) => {
        setActionType(type);
        setActionReason('');
        setActionModal(true);
    };

    const submitAction = async () => {
        if (!selectedVenue || !actionType) return;
        
        setIsSubmitting(true);
        try {
            await auth.fetch(`${config.API_BASE_URL}/api/admin/venues/${selectedVenue.id}/verify`, {
                method: 'POST',
                body: JSON.stringify({
                    status: actionType,
                    notes: actionReason
                })
            });
            
            toast.success(`Venue ${actionType} successfully`);
            setShowModal(false);
            setActionModal(false);
            fetchVenues();
            fetchStats();
        } catch (error) {
            console.error('Error performing action:', error);
            toast.error('Action failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'bg-warning-focus text-warning-main',
            approved: 'bg-success-focus text-success-main',
            rejected: 'bg-danger-focus text-danger-main',
            suspended: 'bg-neutral-200 text-neutral-600'
        };
        return <span className={`badge ${variants[status] || 'bg-primary-50 text-primary-600'} px-8 py-4 radius-4`}>{status}</span>;
    };

    return (
        <div className="d-flex flex-column gap-24">
            {/* Stats Cards */}
            <div className="row gy-4">
                {[
                    { title: 'Total Pending', value: stats.pending, icon: 'mdi:clock-outline', color: 'text-warning-main', bg: 'bg-warning-focus' },
                    { title: 'Total Approved', value: stats.approved, icon: 'mdi:check-circle-outline', color: 'text-success-main', bg: 'bg-success-focus' },
                    { title: 'Total Rejected', value: stats.rejected, icon: 'mdi:close-circle-outline', color: 'text-danger-main', bg: 'bg-danger-focus' },
                    { title: 'Total Venues', value: stats.total, icon: 'mdi:store', color: 'text-primary-main', bg: 'bg-primary-focus' },
                ].map((stat, idx) => (
                    <div className="col-xxl-3 col-sm-6" key={idx}>
                        <div className="card p-16 h-100 radius-12 border-0 shadow-none bg-base d-flex align-items-center gap-12">
                            <div className={`w-48-px h-48-px radius-12 d-flex justify-content-center align-items-center text-xl ${stat.bg} ${stat.color}`}>
                                <Icon icon={stat.icon} />
                            </div>
                            <div>
                                <span className="text-secondary-light text-sm mb-4">{stat.title}</span>
                                <h6 className="text-xl fw-bold mb-0">{stat.value}</h6>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter & List */}
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24">
                    {/* Top row: Title + Bulk Import button */}
                    <div className="d-flex align-items-center justify-content-between mb-12">
                        <h6 className="fw-semibold mb-0">Venue Verification</h6>
                        <button
                            className="btn btn-primary-600 btn-sm d-flex align-items-center gap-2 px-16 py-8 radius-8"
                            onClick={() => { setShowBulkImport(true); setImportResult(null); setCsvFile(null); }}
                            title="Bulk import venues from a CSV file"
                            id="bulk-import-btn"
                        >
                            <Icon icon="mdi:file-upload-outline" className="text-lg" />
                            Bulk Import
                        </button>
                    </div>
                    {/* Bottom row: Filters */}
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                        <select 
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                            value={filters.limit}
                            onChange={(e) => setFilters({...filters, limit: Number(e.target.value)})}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                        <div className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                placeholder="Search Venues"
                                value={filters.search}
                                onChange={(e) => setFilters({...filters, search: e.target.value})}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </div>
                        <select 
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                <div className="card-body p-24">
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table hover-table">
                            <thead>
                                <tr>
                                    <th>Venue</th>
                                    <th>Owner</th>
                                    <th>Submitted Date</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                                ) : venues.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center">No venues found</td></tr>
                                ) : (
                                    venues.map(venue => (
                                        <tr key={venue.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-grow-1">
                                                        <h6 className="text-md mb-0 fw-medium">{venue.title}</h6>
                                                        <span className="text-sm text-secondary-light">{venue.business_name}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getOwnerImageUrl(venue.owner_image) && (
                                                        <img
                                                            src={getOwnerImageUrl(venue.owner_image)}
                                                            alt=""
                                                            className="w-32-px h-32-px rounded-circle me-2 object-fit-cover"
                                                            onError={(e) => {
                                                                e.target.src = '/assets/images/user.png';
                                                            }}
                                                        />
                                                    )}
                                                    <div>
                                                        <h6 className="text-md mb-0 fw-medium">{venue.owner_name}</h6>
                                                        <span className="text-sm text-secondary-light">{venue.owner_email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{new Date(venue.created_at).toLocaleDateString()}</td>
                                            <td>{getStatusBadge(venue.verification_status)}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        className="w-32-px h-32-px bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handleView(venue)}
                                                        title="Review"
                                                    >
                                                        <Icon icon="mdi:file-document-edit-outline" />
                                                    </button>
                                                    <button 
                                                        className="w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                        onClick={() => handlePreview(venue)}
                                                        title="Live Preview"
                                                    >
                                                        <Icon icon="mdi:eye" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between mt-4">
                            <span className="text-sm text-secondary-light">
                                Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, pagination.total)} of {pagination.total} entries
                            </span>
                            <div className="pagination">
                                <button 
                                    className="page-link" 
                                    disabled={filters.page === 1}
                                    onClick={() => setFilters({...filters, page: filters.page - 1})}
                                >
                                    Previous
                                </button>
                                <span className="page-link active">{filters.page}</span>
                                <button 
                                    className="page-link" 
                                    disabled={filters.page === pagination.totalPages}
                                    onClick={() => setFilters({...filters, page: filters.page + 1})}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Venue Verification</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailsLoading || !venueDetails ? (
                        <div className="text-center py-5">Loading details...</div>
                    ) : (
                        <Tabs defaultActiveKey="info" className="mb-3">
                            <Tab eventKey="info" title="Information">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">Business Name</label>
                                        <p className="fw-medium">{venueDetails.venue.business_name}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">Title</label>
                                        <p className="fw-medium">{venueDetails.venue.title}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">License Number</label>
                                        <p className="fw-medium">{venueDetails.venue.license_number || 'N/A'}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">Category</label>
                                        <p className="fw-medium">{venueDetails.venue.business_category || 'N/A'}</p>
                                    </Col>
                                    <Col md={12}>
                                        <label className="form-label text-secondary-light">Description</label>
                                        <p className="text-sm">{venueDetails.venue.description}</p>
                                    </Col>
                                    <Col md={12}>
                                        <label className="form-label text-secondary-light">Address</label>
                                        <p className="text-sm">
                                            {venueDetails.venue.street}, {venueDetails.venue.city}, {venueDetails.venue.state}, {venueDetails.venue.zip}, {venueDetails.venue.country}
                                        </p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">Email</label>
                                        <p className="fw-medium">{venueDetails.venue.email}</p>
                                    </Col>
                                    <Col md={6}>
                                        <label className="form-label text-secondary-light">Phone</label>
                                        <p className="fw-medium">{venueDetails.venue.phone}</p>
                                    </Col>
                                </Row>
                            </Tab>
                            <Tab eventKey="documents" title={`Documents (${venueDetails.documents.length})`}>
                                {venueDetails.documents.length === 0 ? (
                                    <p className="text-center text-secondary-light py-3">No documents uploaded</p>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {venueDetails.documents.map(doc => (
                                            <div key={doc.id} className="d-flex align-items-center justify-content-between p-3 border radius-8">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:file-document-outline" className="text-2xl text-primary-600" />
                                                    <div>
                                                        <p className="mb-0 fw-medium">{doc.document_type || 'Document'}</p>
                                                        <span className="text-xs text-secondary-light">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                                    </div>
                                                </div>
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                    View
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Tab>
                            <Tab eventKey="gallery" title="Gallery">
                                <div className="row g-2">
                                    {venueDetails.gallery.map(img => (
                                        <div className="col-4" key={img.id}>
                                            <img src={config.API_BASE_URL + '/' + img.image_url} alt="" className="w-100 h-100 object-fit-cover radius-8" style={{minHeight: '100px'}} />
                                        </div>
                                    ))}
                                </div>
                            </Tab>
                            <Tab eventKey="logs" title="Audit Trail">
                                <div className="timeline">
                                    {venueDetails.logs.map(log => (
                                        <div key={log.id} className="d-flex gap-3 mb-3">
                                            <div className="d-flex flex-column align-items-center">
                                                <div className="w-8-px h-8-px bg-primary-600 rounded-circle"></div>
                                                <div className="w-1-px bg-neutral-200 flex-grow-1"></div>
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-medium">Status changed to <span className="fw-bold">{log.new_status}</span></p>
                                                <p className="text-xs text-secondary-light mb-1">by {log.admin_name} on {new Date(log.created_at).toLocaleString()}</p>
                                                {log.notes && <p className="text-sm bg-base-50 p-2 radius-4">{log.notes}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                    {selectedVenue && (
                        <Button 
                            variant="info" 
                            className="text-white"
                            onClick={() => {
                                setPreviewId(selectedVenue.id);
                                setShowPreview(true);
                            }}
                        >
                            <Icon icon="mdi:eye" className="me-1" />
                            Live Preview
                        </Button>
                    )}
                    {selectedVenue && (
                        <>
                            {selectedVenue.verification_status !== 'rejected' && (
                                <Button variant="danger" onClick={() => handleActionClick('rejected')}>
                                    {selectedVenue.verification_status === 'approved' ? 'Revoke / Reject' : 'Reject'}
                                </Button>
                            )}
                            {selectedVenue.verification_status !== 'approved' && (
                                <Button variant="success" onClick={() => handleActionClick('approved')}>
                                    {selectedVenue.verification_status === 'rejected' ? 'Reconsider / Approve' : 'Approve'}
                                </Button>
                            )}
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Action Confirmation Modal */}
            <Modal show={actionModal} onHide={() => setActionModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm {actionType === 'approved' ? 'Approval' : 'Rejection'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to {actionType === 'approved' ? 'approve' : 'reject'} this venue?</p>
                    <Form.Group className="mb-3">
                        <Form.Label>{actionType === 'rejected' ? 'Rejection Reason (Required)' : 'Notes (Optional)'}</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            value={actionReason} 
                            onChange={(e) => setActionReason(e.target.value)}
                            required={actionType === 'rejected'}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setActionModal(false)}>Cancel</Button>
                    <Button 
                        variant={actionType === 'approved' ? 'success' : 'danger'} 
                        onClick={submitAction}
                        disabled={isSubmitting || (actionType === 'rejected' && !actionReason.trim())}
                    >
                        {isSubmitting ? 'Processing...' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Preview Modal */}
            <VenuePreviewModal 
                show={showPreview} 
                onHide={() => setShowPreview(false)} 
                venueId={previewId} 
            />

            {/* ── Bulk Import CSV Modal ── */}
            {showBulkImport && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title d-flex align-items-center gap-2">
                                    <Icon icon="mdi:file-upload-outline" className="text-primary-600 text-xl" />
                                    Bulk Import Venues (CSV)
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowBulkImport(false)}
                                />
                            </div>
                            <div className="modal-body">
                                {/* CSV Format Guide */}
                                <div className="alert alert-info d-flex gap-3 align-items-start mb-20" role="alert">
                                    <Icon icon="mdi:information-outline" className="text-xl mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="fw-semibold mb-4">Required CSV column order:</p>
                                        <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                                            owner_email, owner_full_name, owner_phone, venue_business_name, venue_title,
                                            venue_description, venue_category, venue_email, venue_address, venue_city,
                                            venue_state, venue_country, venue_latitude, venue_longitude, venue_image_url,
                                            plan_type, subscription_status
                                        </code>
                                        <p className="text-sm text-secondary-light mt-8 mb-0">
                                            First row must be the header. Venues are created with <strong>approved</strong> status automatically.
                                        </p>
                                    </div>
                                </div>

                                {/* Download Template Link */}
                                <div className="mb-20">
                                    <a
                                        href="data:text/csv;charset=utf-8,owner_email,owner_full_name,owner_phone,venue_business_name,venue_title,venue_description,venue_category,venue_email,venue_address,venue_city,venue_state,venue_country,venue_latitude,venue_longitude,venue_image_url,plan_type,subscription_status%0Aowner1@hubscroll.com,Owner One,+971501234567,My Business,My Venue,A great venue,Restaurant,venue1@hubscroll.com,123 Main St,Dubai,Dubai,UAE,25.2048,55.2708,,premium,active"
                                        download="bulk_import_template.csv"
                                        className="btn btn-sm btn-secondary-600 d-inline-flex align-items-center gap-2"
                                    >
                                        <Icon icon="mdi:download" />
                                        Download CSV Template
                                    </a>
                                </div>

                                {/* File Input */}
                                <div className="mb-20">
                                    <label className="form-label fw-semibold" htmlFor="csvFileInput">Select CSV File</label>
                                    <input
                                        id="csvFileInput"
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="form-control"
                                        onChange={(e) => { setCsvFile(e.target.files[0] || null); setImportResult(null); }}
                                    />
                                    {csvFile && (
                                        <p className="text-sm text-success-main mt-6 mb-0 d-flex align-items-center gap-1">
                                            <Icon icon="mdi:check-circle" />
                                            {csvFile.name} selected ({(csvFile.size / 1024).toFixed(1)} KB)
                                        </p>
                                    )}
                                </div>

                                {/* Import Result */}
                                {importResult && (
                                    <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'}`}>
                                        <p className="fw-semibold mb-8">
                                            {importResult.success ? '✅ Import Complete' : '❌ Import Failed'}
                                        </p>
                                        {importResult.stats && (
                                            <ul className="mb-8 ps-16">
                                                <li>Rows processed: <strong>{importResult.stats.processed}</strong></li>
                                                <li>Owners upserted: <strong>{importResult.stats.owners_upserted}</strong></li>
                                                <li>Venues upserted: <strong>{importResult.stats.venues_upserted}</strong></li>
                                                <li>Subscriptions created: <strong>{importResult.stats.subs_created}</strong></li>
                                            </ul>
                                        )}
                                        {importResult.stats?.errors?.length > 0 && (
                                            <div>
                                                <p className="fw-semibold text-danger-main mb-4">Errors:</p>
                                                <ul className="ps-16 text-sm">
                                                    {importResult.stats.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        {importResult.error && <p className="mb-0">{importResult.error}</p>}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary-600"
                                    onClick={() => setShowBulkImport(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary-600 d-flex align-items-center gap-2"
                                    disabled={!csvFile || importLoading}
                                    onClick={async () => {
                                        if (!csvFile) return;
                                        setImportLoading(true);
                                        setImportResult(null);
                                        try {
                                            const formData = new FormData();
                                            formData.append('csv', csvFile);
                                            const res = await auth.fetch(
                                                `${config.API_BASE_URL}/api/admin/venues/import-csv`,
                                                { method: 'POST', body: formData }
                                            );
                                            const data = await res.json();
                                            setImportResult(data);
                                            if (data.success) {
                                                fetchStats();
                                                fetchVenues();
                                                toast.success('CSV import completed successfully!');
                                            }
                                        } catch (err) {
                                            setImportResult({ success: false, error: err.message });
                                            toast.error('Import failed: ' + err.message);
                                        } finally {
                                            setImportLoading(false);
                                        }
                                    }}
                                >
                                    {importLoading ? (
                                        <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Importing...</>
                                    ) : (
                                        <><Icon icon="mdi:upload" /> Start Import</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VenueVerificationLayer;
